/**
 * Time off (piece 34, *PTO: requests, approval and the calendar*; decision
 * 13030; rows T3871 and T3858). 🔒 A person requests time off from My Info on
 * the web and from the calendar on the phone, through one route; a manager above
 * the job, or HR with the hire permission, decides it on Time Awaiting Approval.
 * 🔒 The balance is the ledger's latest `balanceAfter`, read and never computed
 * here, and the requestable hours net the pending requests off it.
 */

import { fill, t } from '../strings';
import { END_OF_DAY } from '../settings/timeAttendance';
import { addCalendarDays, isCalendarDay } from '../zonedTime';
import type { WorkingPatternDays } from './workingPattern';

/** The person's own balances and requests, relative to the API root: the web's window and the phone's form both read it. */
export const TIME_OFF_MINE_PATH = '/hr/time-off/mine';
/** A new request, relative to the API root; the phone queues it while offline (decision 4560). */
export const TIME_OFF_REQUESTS_PATH = '/hr/time-off/requests';
/** The requests this viewer may decide, read by Time Awaiting Approval's Time Off part. */
export const TIME_OFF_APPROVALS_PATH = '/hr/time-off/approvals';
/** 🔒 Decision 13250: the page the HR group's Time Off Requests row opens, the approver's requests alone. */
export const HR_TIME_OFF_REQUESTS_ROUTE = '/hr/time-off-requests';

/** The requester's cancel of one request. */
export function timeOffCancelPath(requestId: string): string {
  return `${TIME_OFF_REQUESTS_PATH}/${encodeURIComponent(requestId)}/cancel`;
}

/** The approver's Approve of one request. */
export function timeOffApprovePath(requestId: string): string {
  return `${TIME_OFF_APPROVALS_PATH}/${encodeURIComponent(requestId)}/approve`;
}

/** The approver's Reject of one request, with its reason. */
export function timeOffRejectPath(requestId: string): string {
  return `${TIME_OFF_APPROVALS_PATH}/${encodeURIComponent(requestId)}/reject`;
}

/** 🔒 Decision 13251: HR cancels an approved request whose first day has passed, with a reason; relative to the API root. */
export function timeOffBegunCancelPath(requestId: string): string {
  return `${TIME_OFF_APPROVALS_PATH}/${encodeURIComponent(requestId)}/cancel`;
}

/** A day nobody's pattern names counts this many hours, Monday to Friday (decision 13030). */
export const STANDARD_DAY_HOURS = 8;
/** 🔨 The least a typed figure for a part day holds, a quarter hour (decision 13030). */
export const LEAST_TYPED_HOURS = 0.25;

/** The confirmation and the rejection notice, each an `EmailTemplate` row the seed writes (decisions 0118, 13030). */
export const TIME_OFF_APPROVED_TEMPLATE = {
  name: 'Time Off Approved',
  subject: 'Your time off from {{firstDay}} to {{lastDay}} is approved',
  bodyRichText: 'Hello {{personName}},\n\nYour request for {{hours}} hours of {{planName}} from {{firstDay}} to {{lastDay}} is approved. It is on your calendar.',
  documentType: 'PTORequest',
} as const;
export const TIME_OFF_REJECTED_TEMPLATE = {
  name: 'Time Off Not Approved',
  subject: 'Your time off from {{firstDay}} to {{lastDay}} is not approved',
  bodyRichText: 'Hello {{personName}},\n\nYour request for {{hours}} hours of {{planName}} from {{firstDay}} to {{lastDay}} is not approved. The reason given: {{reason}}',
  documentType: 'PTORequest',
} as const;

/** 🔒 The refusal of a decision to anybody but a manager above the job or HR with the hire permission. */
export const TIME_OFF_DECIDE_REFUSAL = 'A time-off request is decided by a manager above this person or by HR with the hire permission, and this identity is neither.';
/** 🔨 Nobody decides their own request. */
export const TIME_OFF_OWN_REFUSAL = 'Nobody decides their own time-off request.';

/** 🔒 The refusal of a begun request's cancel to anybody but HR holding the hire permission, the requester included (decision 13251). */
export const TIME_OFF_BEGUN_CANCEL_REFUSAL = 'A time-off request that has begun is canceled by HR with the hire permission, and this identity is not that.';
/** 🔨 The least a begun request's cancel reason holds, and the most. */
export const TIME_OFF_BEGUN_REASON_MAXIMUM = 500;

/** Where one request stands: its approval, or Cancelled once `cancelledAt` is stamped. */
export const TIME_OFF_STATUSES = ['Pending', 'Approved', 'Rejected', 'Cancelled'] as const;
export type TimeOffStatus = (typeof TIME_OFF_STATUSES)[number];

/** One plan's balance as the person reads it: each figure in hours. */
export interface TimeOffPlanBalance {
  planId: string;
  planName: string;
  /** 🔒 The latest `PTOLedgerEntry.balanceAfter`, read rather than computed (decision 0110); nought where the ledger holds no row. */
  available: number;
  /** The hours of this plan's pending requests, reserved and not yet spent. */
  pending: number;
  /** What a new request may take: the balance less the pending hours (decision 13030). */
  requestable: number;
  /** The hours of approved requests starting this calendar year. */
  usedThisYear: number;
  /** The plan books time not yet earned, so no request is refused on its balance. */
  allowNegativeBalance: boolean;
}

/** One request, as My Info lists it and the approver reads it. */
export interface TimeOffRequestRow {
  id: string;
  planId: string;
  planName: string;
  /** `YYYY-MM-DD`, the first and the last day off. */
  startDate: string;
  endDate: string;
  hours: number;
  status: TimeOffStatus;
  note: string | null;
  rejectionReason: string | null;
  requestedAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  /** 🔒 The requester may cancel it: pending, or approved before its first day (decision 13030). */
  mayCancel: boolean;
}

/** The person's own time off, as `GET /hr/time-off/mine` answers it. */
export interface TimeOffMine {
  /** The job the requests are on; null where the person holds none at our own Company. */
  employmentId: string | null;
  /** The plans the job is enrolled in today, each with its balance. */
  plans: TimeOffPlanBalance[];
  /** Newest first. */
  requests: TimeOffRequestRow[];
  /** The Organization's today, `YYYY-MM-DD`, the earliest first day a request may name. */
  today: string;
}

/** What the request window and the phone's form send. */
export interface TimeOffDraft {
  planId: string;
  /** `YYYY-MM-DD`. */
  startDate: string;
  endDate: string;
  /** The hours typed for a part day; empty counts each working day in full. */
  hours: string;
  note: string;
}

export type TimeOffField = keyof TimeOffDraft;

/** A fresh draft on a day: one plan chosen where there is one, the day as both ends. */
export function emptyTimeOffDraft(plans: readonly Pick<TimeOffPlanBalance, 'planId'>[], day: string): TimeOffDraft {
  return { planId: plans.length === 1 ? plans[0]!.planId : '', startDate: day, endDate: day, hours: '', note: '' };
}

/** Every calendar day from the first to the last, both included; none where the order is wrong. */
export function timeOffDays(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  for (let day = startDate; day <= endDate && days.length < 366; day = addCalendarDays(day, 1)) days.push(day);
  return days;
}

/** A typed hours figure as a number, or null where it is not one. */
export function typedHours(hours: string): number | null {
  const trimmed = hours.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * 🔨 What stops a request, by field, in a sentence each (decision 13030): a plan, two real days in order, the first
 * today or later, at most a year, and typed hours of a quarter hour or more with two places at most.
 * 🔒 Given the days as the server counts them, a typed figure is held to the hours they count, and days that count
 * none are refused whatever is typed, naming the day where it is the only one. The web and the phone hold no working
 * pattern or holiday table, so they pass no count and hold a typed figure to a day's twenty-four hours alone.
 */
export function timeOffDraftProblems(draft: TimeOffDraft, today: string, counted?: readonly { day: string; hours: number }[]): Partial<Record<TimeOffField, string>> {
  const problems: Partial<Record<TimeOffField, string>> = {};
  if (!draft.planId) problems.planId = t('hr.timeOff.problem.plan');
  if (!isCalendarDay(draft.startDate)) problems.startDate = t('hr.timeOff.problem.day');
  else if (draft.startDate < today) problems.startDate = t('hr.timeOff.problem.past');
  if (!isCalendarDay(draft.endDate)) problems.endDate = t('hr.timeOff.problem.day');
  else if (isCalendarDay(draft.startDate) && draft.endDate < draft.startDate) problems.endDate = t('hr.timeOff.problem.order');
  else if (isCalendarDay(draft.startDate) && timeOffDays(draft.startDate, draft.endDate).length >= 366) problems.endDate = t('hr.timeOff.problem.long');
  const daysRead = !problems.startDate && !problems.endDate;
  const total = counted ? Math.round(counted.reduce((sum, one) => sum + one.hours, 0) * 100) / 100 : null;
  // 🔨 Decision 13030: a request whose days count no hours is refused, and a typed figure does not make it a working day.
  if (daysRead && total === 0) {
    problems.startDate = draft.startDate === draft.endDate ? fill(t('hr.timeOff.problem.dayCountsNone'), { day: draft.startDate }) : t('hr.timeOff.problem.daysCountNone');
  } else if (draft.hours.trim() !== '') {
    const hours = typedHours(draft.hours);
    if (hours === null || hours < LEAST_TYPED_HOURS) problems.hours = t('hr.timeOff.problem.hours');
    else if (daysRead && total !== null && hours > total) problems.hours = fill(t('hr.timeOff.problem.overCounted'), { counted: total });
    else if (daysRead && hours > 24 * timeOffDays(draft.startDate, draft.endDate).length) problems.hours = t('hr.timeOff.problem.tooMany');
  }
  if (draft.note.length > 500) problems.note = t('hr.timeOff.problem.note');
  return problems;
}

/** One pattern day's hours, its end less its start; nought for a day not worked. */
function patternDayHours(day: WorkingPatternDays[number]): number {
  if (!day) return 0;
  const minutes = (clock: string) => (clock === END_OF_DAY ? 1440 : Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5)));
  return Math.max(0, minutes(day.endsAt) - minutes(day.startsAt)) / 60;
}

/**
 * 🔒 The hours one day counts (decision 13030): nothing on a holiday; the working pattern's hours that weekday where
 * a pattern is in force; else eight Monday to Friday and nothing at the weekend.
 */
export function timeOffDayHours(day: string, pattern: WorkingPatternDays | null, holidays: ReadonlySet<string>): number {
  if (holidays.has(day)) return 0;
  const weekday = new Date(`${day}T00:00:00.000Z`).getUTCDay();
  if (pattern) return Math.round(patternDayHours(pattern[weekday] ?? null) * 100) / 100;
  return weekday === 0 || weekday === 6 ? 0 : STANDARD_DAY_HOURS;
}

/** Each day of a span with the hours it counts, read through the pattern in force on that day. */
export function countedTimeOffDays(startDate: string, endDate: string, patternOn: (day: string) => WorkingPatternDays | null, holidays: ReadonlySet<string>): { day: string; hours: number }[] {
  return timeOffDays(startDate, endDate).map((day) => ({ day, hours: timeOffDayHours(day, patternOn(day), holidays) }));
}

/** One requested day on the approver's item (decision 0231): who on the team is off, approved and pending, and who is left. */
export interface TimeOffTeamDay {
  day: string;
  /** The day counts no hours on this request: a weekend, a day off or a holiday. */
  off: boolean;
  approved: string[];
  pending: string[];
  /** The team as the chain draws it that day. */
  teamSize: number;
  /** The headcount left were this request approved too. */
  left: number;
}

/** One request waiting on the approver, with the picture decision 0231 asks for. */
export interface TimeOffApprovalItem {
  request: TimeOffRequestRow;
  employmentId: string;
  name: string;
  /** The requester's requestable hours under the plan before this request, and what is left after it. */
  requestable: number;
  after: number;
  days: TimeOffTeamDay[];
}

export interface TimeOffApprovalsAnswer {
  items: TimeOffApprovalItem[];
}

/** A status as its pill reads it. */
export function timeOffStatusLabel(status: TimeOffStatus): string {
  return t(`hr.timeOff.status.${status.toLowerCase() as 'pending' | 'approved' | 'rejected' | 'cancelled'}`);
}

/** The pill's group for a status. */
export function timeOffStatusGroup(status: TimeOffStatus): 'good' | 'warning' | 'error' | 'inactive' {
  return status === 'Approved' ? 'good' : status === 'Pending' ? 'warning' : status === 'Rejected' ? 'error' : 'inactive';
}

/** Hours as a balance reads them, two places at most. */
export function timeOffHoursText(hours: number): string {
  return `${Math.round(hours * 100) / 100} ${t('hr.timeOff.hoursUnit')}`;
}

/** One approved request under way, as HR reads it on the Employee page (decision 13251). */
export interface TimeOffBegunRow {
  requestId: string;
  planName: string;
  /** `YYYY-MM-DD`, the request's first and last day. */
  startDate: string;
  endDate: string;
  hours: number;
  /** The first unused day, the day after the Organization's today; the days from it on are canceled. */
  from: string;
  /** The hours the Adjustment row gives back: the request's hours in proportion to the counted hours of the unused days. */
  hoursReturned: number;
}

/** 🔒 Decision 13251: the approved requests under way on a job, sent to HR reading as HR with `hr.hire` and to nobody else. */
export interface TimeOffBegunSection {
  rows: TimeOffBegunRow[];
}

/** What HR's cancel of a begun request answers: the hours given back and the events canceled. */
export interface TimeOffBegunCancelAnswer {
  requestId: string;
  hoursReturned: number;
  events: number;
}
